import { createAuth0 } from '@auth0/auth0-vue';

export const clearAuth0LocalStorage = () => {
    Object.keys(localStorage)
        .filter((key) => key.startsWith('@@auth0spajs@@'))
        .forEach((key) => localStorage.removeItem(key));
};

/**
 * Captures Auth0 redirect-callback errors from the URL at module load —
 * before app.use(auth) installs the SDK and strips ?error=&state= via
 * replaceState. Unlike auth.error (set by every failed SDK call via
 * __proxy), this only reflects a hard login/redirect-callback failure.
 */
const captureAuthCallbackError = () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') && params.get('state')) {
        return {
            error: params.get('error'),
            error_description: params.get('error_description') || '',
        };
    }
    return null;
};

export const authCallbackError = captureAuthCallbackError();

const readAuth0Config = () => {
    const config = {
        domain: import.meta.env.VITE_AUTH_DOMAIN,
        clientId: import.meta.env.VITE_ACCOUNT_CENTRAL_OIDC_CLIENT_ID,
        audience: import.meta.env.VITE_AUTH_AUDIENCE,
    };

    const missing = Object.entries(config)
        .filter(([, value]) => !value || String(value).startsWith('TODO_'))
        .map(([key]) => key);

    if (missing.length) {
        throw new Error(
            `Auth0 SPA application is not provisioned for env "${import.meta.env.VITE_ENV}": `
            + `missing or placeholder values for ${missing.join(', ')}. `
            + `Update .env.${import.meta.env.VITE_ENV || 'localdev'} with the Auth0 domain, client_id, and audience.`,
        );
    }

    return config;
};

const { domain, clientId, audience } = readAuth0Config();

export const auth = createAuth0({
    domain,
    clientId,
    // localStorage enables token persistence across page refreshes without a spinner.
    // Trade-off: tokens are XSS-readable. Mitigated by short-lived access tokens +
    // refresh token rotation. Switch to 'memory' if XSS risk becomes a concern.
    cacheLocation: 'localstorage',
    useRefreshTokens: true,
    // Falls back to silent-iframe auth when refresh-token cookies are
    // blocked (Safari ITP, strict-tracking-prevention browsers). Without
    // this, those users get a hard logout when the SDK can't rotate.
    useRefreshTokensFallback: true,
    authorizationParams: {
        audience,
        redirect_uri: window.location.origin,
        scope: 'openid profile email security_profile keap_profile offline_access',
    },
}, { errorPath: '/auth-error' },
);

// Async access-token getter. Maps `ignoreCache` to the SDK's `cacheMode: 'off'`
// so callers (axios interceptor, etc.) can force a fresh token on 401 retry
// without exposing the raw SDK option to every consumer.
export async function getAccessToken({ ignoreCache = false } = {}) {
    return auth.getAccessTokenSilently(
        ignoreCache ? { cacheMode: 'off' } : undefined,
    );
}

const _originalLogout = auth.logout.bind(auth);
auth.logout = (options) => {
    clearAuth0LocalStorage();
    sessionStorage.removeItem('legacy_link_url');
    return _originalLogout(options);
};
